import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import './common.module.css';
import './wizard.module.css';
import './App.css'
import PrivateRoute from './PrivateRoutes';
import UserAuth from './Users/Auth';
import HomePage from './HomePage';
import LoginPage from './Users/Login-Signup';
import RegisterPage from './Users/Registration';
import DashboardPage from './Users/Dashboard';
import Config from './Config';
import InfoSection from './pages/Info';
import ExternalDocsSection from './pages/Documentation';
import ServersSection from './pages/Servers';
import TagsSection from './pages/Tags';
import SecuritySection from './pages/security/Security';
import SchemasSection from './pages/schemas/Schemas';
import ExamplesSection from './pages/Examples';
import RequestBodiesSection from './pages/RequestBodies';
import ResponseBodiesSection from './pages/ResponseBodies';
import HeadersSection from './pages/headers/Headers';
import ParametersSection from './pages/Parameters/Parameters';
import PathsSection from './pages/Paths/Paths';
import DownloadPage from './pages/Download/Decision';
import NavigationButtons from './components/NavigationButtons';
import AssetsPage from './Users/Assets';
import UserLogout from './Users/Logout';
import SessionHandler from './Users/Sessions';
import UserProfile from './Users/Profile';
import UserAgreement from './Users/UserAgreement';
import { useFormData } from './hooks/useFormData';

function App() {
  const [selectedVersion, setSelectedVersion] = useState("3.0.3");
  const {
    formData,
    setFormData,
    handleInputChange,
    handleNestedInputChange,
    handleArrayChange,
    addServer,
    removeServer,
    addTag,
    handleTagChange,
    handleTagExternalDocsChange,
    handleSecuritySchemeChange,
    removeTag,
    addSchema,
    editSchema,
    removeSchema,
    addHeader,
    editHeader,
    removeHeader,
    addExample,
    editExample,
    removeExample,
    addResponse,
    editResponse,
    removeResponse,
    addRequestBody,
    editRequestBody,
    removeRequestBody,
    addParameter,
    editParameter,
    removeParameter,
    addPath,
    editPath,
    removePath,
  } = useFormData(selectedVersion);

  const [currentPage, setCurrentPage] = useState(0);
  const totalPages = 12;

  const handlePageChange = (direction) => {
    setCurrentPage((prevPage) => {
      if (direction === 'next' && prevPage < totalPages) {
        return prevPage + 1;
      } else if (direction === 'prev' && prevPage > 0) {
        return prevPage - 1;
      }
      return prevPage;
    });
  };

  const handleVersionSelect = (version) => {
    resetForm();
    setSelectedVersion(version);
    setFormData((prev) => ({
      ...prev,
      openapi: version,
      info: {
        ...prev.info,
        summary: version === "3.1.0" ? prev.info.summary || '' : undefined,
        license: {
          ...prev.info.license,
          identifier: version === "3.1.0" ? prev.info.license?.identifier || '' : undefined,
        },
      },
    }));
    setCurrentPage(0);
  };

  const resetForm = () => {
    setFormData({
      openapi: '',
      info: {
        title: '',
        description: '',
        summary: selectedVersion === "3.1.0" ? '' : undefined,
        version: '',
        termsOfService: '',
        contact: { name: '', url: '', email: '' },
        license: { name: '', url: '', identifier: selectedVersion === "3.1.0" ? '' : undefined },
      },
      externalDocs: { description: '', url: '' },
      servers: [],
      tags: [],
      paths: {},
      components: {
        schemas: {},
        headers: {},
        examples: {},
        requestBodies: {},
        responses: {},
        parameters: {},
        securitySchemes: {},
      },
    });
  };

  useEffect(() => {
    const savedFormData = localStorage.getItem('formData');
    if (savedFormData) {
      try {
        const parsedFormData = JSON.parse(savedFormData);
        setFormData((prevData) => ({
          ...prevData,
          ...parsedFormData,
          servers: Array.isArray(parsedFormData.servers) ? parsedFormData.servers : [],
        }));
      } catch (error) {
        console.error('Failed to parse formData from localStorage:', error);
      }
    }
  }, [setFormData]);

  useEffect(() => {
    localStorage.setItem('formData', JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    const handleContextMenu = (e) => {
      if (e.target.closest('.json-output')) {
        e.preventDefault();
      }
    };

    window.addEventListener('contextmenu', handleContextMenu);

    return () => {
      window.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  return (
    <Router>
      <Routes>
        {/* URL-based routing */}
        <Route path="/" element={<HomePage />} />
        <Route path="/authenticate" element={<UserAuth />} />
        <Route path="/login-signup" element={<LoginPage />} />

        <Route element={<PrivateRoute />}>
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/user-agreement" element={<UserAgreement />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/user/assets" element={<AssetsPage />} />
          <Route path="/config" element={<Config onVersionSelect={handleVersionSelect} />} />
          <Route path="/user/logout" element={<UserLogout />} />
          <Route path="/user/profile" element={<UserProfile />} />
          <Route path="/user/sessions" element={<SessionHandler />} />

          {/* State-based navigation for subsequent pages */}
          <Route
            path="/wizard/*"
            element={
              <div className="app-container">
                <div className='main-content'>
                  <div className="form-container">
                    {currentPage === 0 && (
                      <InfoSection
                        key={selectedVersion}
                        formData={formData.info}
                        handleInputChange={handleInputChange}
                        handleNestedInputChange={handleNestedInputChange}
                        selectedVersion={selectedVersion}
                      />
                    )}
                    {currentPage === 1 && (
                      <ExternalDocsSection
                        formData={formData.externalDocs}
                        handleInputChange={handleInputChange}
                      />
                    )}
                    {currentPage === 2 && (
                      <ServersSection
                        formData={formData}
                        handleArrayChange={handleArrayChange}
                        addServer={addServer}
                        removeServer={removeServer}
                      />
                    )}
                    {currentPage === 3 && (
                      <TagsSection
                        tags={formData.tags}
                        handleTagChange={handleTagChange}
                        addTag={addTag}
                        handleTagExternalDocsChange={handleTagExternalDocsChange}
                        removeTag={removeTag}
                      />
                    )}
                    {currentPage === 4 && (
                      <SecuritySection
                        securitySchemes={formData.components.securitySchemes || {}}
                        handleSecuritySchemeChange={handleSecuritySchemeChange}
                      />
                    )}
                    {currentPage === 5 && (
                      <SchemasSection
                        schemas={formData.components.schemas || {}}
                        addSchema={addSchema}
                        editSchema={editSchema}
                        removeSchema={removeSchema}
                      />
                    )}
                    {currentPage === 6 && (
                      <HeadersSection
                        headers={formData.components.headers || {}}
                        addHeader={addHeader}
                        editHeader={editHeader}
                        removeHeader={removeHeader}
                      />
                    )}
                    {currentPage === 7 && (
                      <ExamplesSection
                        examples={formData.components.examples || {}}
                        addExample={addExample}
                        editExample={editExample}
                        removeExample={removeExample}
                      />
                    )}
                    {currentPage === 8 && (
                      <ResponseBodiesSection
                        responses={formData.components.responses || {}}
                        addResponse={addResponse}
                        editResponse={editResponse}
                        removeResponse={removeResponse}
                        existingHeaders={formData.components.headers || {}}
                        schemas={Object.keys(formData.components.schemas || {})}
                        examples={Object.keys(formData.components.examples || {})}
                      />
                    )}
                    {currentPage === 9 && (
                      <RequestBodiesSection
                        requests={formData.components.requestBodies || {}}
                        addRequestBody={addRequestBody}
                        editRequestBody={editRequestBody}
                        removeRequestBody={removeRequestBody}
                        schemas={Object.keys(formData.components.schemas || {})}
                        examples={Object.keys(formData.components.examples || {})}
                      />
                    )}
                    {currentPage === 10 && (
                      <ParametersSection
                        parameters={formData.components.parameters || {}}
                        addParameter={addParameter}
                        editParameter={editParameter}
                        removeParameter={removeParameter}
                      />
                    )}
                    {currentPage === 11 && (
                      <PathsSection
                        paths={formData.paths || {}}
                        addPath={addPath}
                        editPath={editPath}
                        removePath={removePath}
                        existingTags={formData.tags || []}
                        existingParameters={Object.keys(formData.components.parameters || {})}
                        existingResponses={Object.keys(formData.components.responses || {})}
                        existingSecuritySchemes={formData.components.securitySchemes || {}}
                        existingRequestBody={Object.keys(formData.components.requestBodies || {})}
                      />
                    )}
                    {currentPage === 12 && (
                      <DownloadPage
                        jsonData={formData}
                      />
                    )}
                    <NavigationButtons
                      currentPage={currentPage}
                      totalPages={totalPages}
                      handlePageChange={handlePageChange}
                    />
                  </div>
                  <div className="json-output">
                    <pre>{JSON.stringify(formData, null, 2)}</pre>
                  </div>
                </div>
                <div className="navbar">
                  <a href="/dashboard">Home</a>
                  <div className="account-container">
                    <a href="#" className="account-link">My Account</a>
                    <div className="account-dropdown">
                      <a href="/user/profile">Profile</a>
                      <a href="/user/sessions">Sessions</a>
                    </div>
                  </div>
                  <a href="/user/logout">Logout</a>
                </div>
              </div>
            }
          />
        </Route>
        <Route path="*" element={<div>404 Not Found</div>} />
      </Routes>
    </Router>
  );
}

export default App;
